"use client";

import Icon from "@afa-shared/afa-components/dist/Icon";
import { ContentLink } from "@components/Link/ContentLink";
import { generateUUID } from "@utils/uuid";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";

import leftMenuStyles from "./StandardPageLeftMenu.module.css";

export interface ILeftMenuItem {
  id: string;
  name: string;
  slug: string;
  url: string;
  level: number;
  isContainer: boolean;
  children: Array<ILeftMenuItem>;
}

export const StandardPageLeftMenu = (props) => {
  const firstLevelItems = props?.leftmenuItems as ILeftMenuItem[];
  const pathName = usePathname() ?? "";
  const [displayChildren, setDisplayChildren] = useState<any>({});
  const ref = useRef(null);

  const openSubMenuRecursivly = (item: ILeftMenuItem) => {
    if (itemIsActive(item) || hasActiveChildren(item)) {
      openChildren(item);
      item?.children?.map((child: ILeftMenuItem) => openSubMenuRecursivly(child));
    } else {
      setDisplayChildren((displayChildren) => {
        return {
          ...displayChildren,
          [item.id]: false,
        };
      });
    }
  };

  useEffect(() => {
    firstLevelItems?.map((item: ILeftMenuItem) => {
      openSubMenuRecursivly(item);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //An item is never "active" in a leftmenu if it is a container
  const itemIsActive = (item: ILeftMenuItem): boolean =>
    item.isContainer === false && item.url === pathName;
  const hasActiveChildren = (item: ILeftMenuItem): boolean =>
    item.children?.some((child: ILeftMenuItem) =>
      itemIsActive(child) ? true : hasActiveChildren(child),
    );

  const openChildren = (item: ILeftMenuItem) => {
    setDisplayChildren((displayChildren) => {
      return {
        ...displayChildren,
        [item.id]: true,
      };
    });
  };

  const handleClick = (item: ILeftMenuItem) => {
    setDisplayChildren({
      ...displayChildren,
      [item.id]: !displayChildren[item.id],
    });
  };

  const GetLeftMenuItem = (item: ILeftMenuItem, level: number): React.ReactNode => {
    const hasChildren = item?.children?.length > 0;
    const areaId = generateUUID();

    return (
      <li className={leftMenuStyles.listItem} key={item.id} data-level={level}>
        <div className={leftMenuStyles.itemWrapper}>
          <ContentLink
            href={item?.url}
            className={`${leftMenuStyles.link} ${itemIsActive(item) ? leftMenuStyles.active : ""} ${
              hasActiveChildren(item) ? leftMenuStyles.bold : ""
            }`}
            contentText={item?.name}
          >
            <span
              className={`${leftMenuStyles.decorator} ${
                itemIsActive(item) ? leftMenuStyles.active : ""
              }`}
              data-level={level}
            ></span>
            <span
              className={`${leftMenuStyles.span} ${
                item?.level === 0 ? leftMenuStyles.levelZeroLabel : ""
              }`}
            >
              {item?.name}
            </span>
          </ContentLink>

          {hasChildren ? (
            <button
              className={`${leftMenuStyles.iconButton} ${
                hasChildren ? leftMenuStyles.hasChildren : ""
              }`}
              onClick={() => handleClick(item)}
              aria-expanded={displayChildren[item.id] ? displayChildren[item.id] : false}
              aria-controls={areaId}
              aria-label={`Växla undermeny för ${item?.name}`}
            >
              <Icon name={displayChildren[item.id] ? "remove" : "add"} size="sm" />
            </button>
          ) : null}
        </div>

        {hasChildren && (
          <div
            className={`${leftMenuStyles.childWrapper} ${
              displayChildren[item?.id] ? leftMenuStyles.isOpen : ""
            }`}
            data-level={level}
            id={areaId}
          >
            <ul className={leftMenuStyles.list} ref={ref}>
              {item?.children?.map((childItem: ILeftMenuItem) => {
                return GetLeftMenuItem(childItem, level + 1);
              })}
            </ul>
          </div>
        )}
      </li>
    );
  };

  return (
    <nav
      aria-label={"Vänstermeny"}
      style={{ position: "relative", height: "100%" }}
      data-contentful-entry-id={props.contentfulPreviewEntryId}
      data-contentful-field-id={props.contentfulPreviewLeftMenuFieldId}
    >
      <ul className={leftMenuStyles.leftMenu} data-level={1}>
        <li className={leftMenuStyles.listItem} data-level={0}>
          <div className={leftMenuStyles.itemWrapper}>
            <ContentLink
              href={firstLevelItems[0]?.url}
              className={`${leftMenuStyles.link} ${
                pathName === firstLevelItems[0]?.url ? leftMenuStyles.active : ""
              }`}
              contentText={firstLevelItems[0]?.name}
            >
              <span
                className={`${leftMenuStyles.decorator} ${
                  pathName === firstLevelItems[0]?.url ? leftMenuStyles.active : ""
                }`}
                data-level={0}
              ></span>
              <span className={leftMenuStyles.span}>{firstLevelItems[0]?.name}</span>
            </ContentLink>
          </div>
        </li>
        {firstLevelItems[0]?.children?.map((item: ILeftMenuItem) => GetLeftMenuItem(item, 0))}
      </ul>
    </nav>
  );
};
export default StandardPageLeftMenu;
